import * as z from 'zod';

// Storage Service — presigned direct-to-object-storage uploads.
//
// The endpoint (`POST /uploads/presign`) is still absent from the merchant
// spec (`apps/merchant-panel/openapi.json`) and STALE in the admin one
// (`openapi-admin.json` documents a single-file body two revisions old), but
// it is served by the same gateway, so the merchant panel calls it on the
// shared base URL like every other endpoint here. The contract below is the
// backend team's announcement of 2026-08-18.
//
// The flow is three-legged and the browser never streams bytes through our API:
//
//   1. POST `uploads/presign` `{ count, bucket, folder }`
//      → `{ list: [{ upload_url, key, public_url, … }, …] }` — `count` slots
//   2. PUT each raw `File` to an `upload_url` (object storage, NOT our gateway)
//   3. persist each `key` on the product/restaurant/banner being saved
//
// Step 1 changed shape on 2026-08-18: the batch of `{filename, folder}` items
// is GONE, replaced by a COUNT of interchangeable slots (1–10) plus a bucket
// and folder for the whole batch. Nothing names an individual file anymore —
// the slots carry no per-file identity, so any returned slot may serve any
// file. `bucket` is always `"eats"` for now, and `folder` is strictly one of
// `product` / `restaurant` / `banners` (SINGULAR — the pre-2026-08-18 plural
// values are rejected, as is any folder not on the list; clients cannot mint
// their own).
//
// Step 3 sends the KEY, not `public_url`: the key is the object's durable
// identity, while the public URL is a rendering of it that depends on the
// bucket's host, so persisting the URL would bake today's S3 hostname into
// every stored row. The backend expands the key back to an absolute URL on
// read, so the read schemas and every `<img src>` still see a full URL.
//
// Only step 1 goes through `@noor/api`'s `http`. Step 2 must bypass it: the
// presigned URL is absolute, carries its own signature in the query string, and
// signs a specific `Content-Type` — an `Authorization` header or a rewritten
// body would invalidate it.

/** The most slots one presign call may request — `count` is capped at 10. */
export const PRESIGN_MAX_BATCH = 10;

/** The only bucket the gateway currently accepts. */
export const UPLOAD_BUCKET = 'eats';

/** Request body for `POST /uploads/presign` — `count` interchangeable slots. */
export const presignRequestSchema = z.object({
  /** How many upload URLs to mint, 1–10. */
  count: z.number().int().min(1).max(PRESIGN_MAX_BATCH),
  /** Always {@link UPLOAD_BUCKET} today; the field exists for future buckets. */
  bucket: z.string(),
  /** Destination prefix — strictly `product` | `restaurant` | `banners`. */
  folder: z.string(),
});
export type PresignRequest = z.infer<typeof presignRequestSchema>;

/**
 * One slot of the presign response `list`. Every field is optional on the wire
 * (protobuf-JSON omits zero values), so each defaults rather than failing the
 * parse; the caller checks `upload_url`/`key` before using the slot.
 *
 * Slots are INTERCHANGEABLE: the request names no files, so no slot is tied to
 * one — the caller assigns them to files in whatever order it likes. `index`
 * and `error` are kept from the item-batch era and still honoured when sent:
 * a slot carrying an `error` fails the batch even under a 200.
 */
export const presignResponseSchema = z.object({
  /** Short-lived signed PUT target in object storage. */
  upload_url: z.string().default(''),
  /** The object's key within the bucket — this is what gets persisted. */
  key: z.string().default(''),
  /**
   * Absolute URL the key resolves to. Useful for an immediate local preview,
   * but NOT what the write endpoints take — see the flow note above.
   */
  public_url: z.string().default(''),
  /** Slot position; informational now that slots carry no file identity. */
  index: z.number().int().default(0),
  /** Echo of the requested folder. */
  folder: z.string().default(''),
  /** Per-slot failure. Empty means this slot is usable. */
  error: z.string().default(''),
});
export type PresignResponse = z.infer<typeof presignResponseSchema>;
